// Copyright 2025-2026 Florian Gallon. All Rights reserved.
import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

/**
 * Editorial Toast Notification Component
 * Displays toast messages with warm, artisanal editorial styling
 * Now supports auto-dismiss, manual close, progress bar, and hover pause
 *
 * @param {number} id - Unique toast ID
 * @param {string} type - Type of toast: 'success', 'error', 'warning', 'info'
 * @param {string} message - The message to display
 * @param {number} duration - Duration in ms (0 = no auto-dismiss)
 * @param {Object} action - Optional action button { label, onClick }
 * @param {function} onClose - Close callback
 * @param {function} onPause - Pause timer callback
 * @param {function} onResume - Resume timer callback
 */
const Toast = ({
  id,
  type = 'success',
  message,
  duration = 0,
  action,
  onClose,
  onPause,
  onResume
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const startTimeRef = useRef(Date.now());
  const remainingTimeRef = useRef(duration);
  const progressIntervalRef = useRef(null);

  // Editorial color palette for toasts
  const config = {
    success: {
      background: 'linear-gradient(135deg, var(--brand-sage) 0%, var(--brand-sage-dark) 100%)',
      icon: CheckCircle,
      textColor: 'var(--brand-warm-white)',
      progressColor: 'var(--brand-sage-dark)'
    },
    error: {
      background: 'linear-gradient(135deg, var(--status-danger) 0%, var(--status-danger-hover) 100%)',
      icon: XCircle,
      textColor: 'var(--brand-warm-white)',
      progressColor: 'var(--status-danger-hover)'
    },
    warning: {
      background: 'linear-gradient(135deg, var(--brand-terracotta-light) 0%, var(--brand-terracotta-dark) 100%)',
      icon: AlertTriangle,
      textColor: 'var(--brand-charcoal)',
      progressColor: 'var(--brand-terracotta-dark)'
    },
    info: {
      background: 'linear-gradient(135deg, var(--brand-cream) 0%, var(--brand-cream-dark) 100%)',
      icon: Info,
      textColor: 'var(--brand-charcoal)',
      border: '2px solid var(--brand-terracotta)',
      progressColor: 'var(--brand-terracotta)'
    }
  };

  const { background, icon: Icon, textColor, border, progressColor } = config[type] || config.success;

  // Entrance animation
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  // Progress bar animation
  useEffect(() => {
    if (duration > 0) {
      startTimeRef.current = Date.now();
      remainingTimeRef.current = duration;

      progressIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current;
        const remaining = Math.max(0, duration - elapsed);
        const progressPercent = (remaining / duration) * 100;
        setProgress(progressPercent);

        if (remaining <= 0) {
          clearInterval(progressIntervalRef.current);
        }
      }, 16); // ~60fps

      return () => {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }
      };
    }
  }, [duration]);

  // Handle close with exit animation
  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match animation duration
  };

  // Handle mouse enter (pause timer)
  const handleMouseEnter = () => {
    if (duration > 0 && onPause) {
      const elapsed = Date.now() - startTimeRef.current;
      remainingTimeRef.current = Math.max(0, duration - elapsed);
      onPause();

      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    }
  };

  // Handle mouse leave (resume timer)
  const handleMouseLeave = () => {
    if (duration > 0 && onResume) {
      startTimeRef.current = Date.now();
      onResume(remainingTimeRef.current);

      // Resume progress animation
      progressIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current;
        const remaining = Math.max(0, remainingTimeRef.current - elapsed);
        const progressPercent = (remaining / remainingTimeRef.current) * 100;
        setProgress(progressPercent);

        if (remaining <= 0) {
          clearInterval(progressIntervalRef.current);
        }
      }, 16);
    }
  };

  return (
    <div
      className={`
        transition-all duration-300 ease-out
        ${isVisible && !isExiting ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="relative rounded-2xl shadow-lifted overflow-hidden min-w-[320px] max-w-md"
        style={{
          background,
          border: border || 'none',
          backdropFilter: 'blur(10px)'
        }}
      >
        {/* Content */}
        <div className="px-6 py-4 flex items-start gap-3">
          <Icon size={24} className="flex-shrink-0 mt-0.5" strokeWidth={2.5} style={{ color: textColor }} />

          <div className="flex-1 min-w-0">
            <p className="font-semibold text-base tracking-wide break-words" style={{ color: textColor }}>
              {message}
            </p>

            {/* Optional Action Button */}
            {action && (
              <button
                onClick={() => {
                  action.onClick();
                  handleClose();
                }}
                className="mt-2 text-sm font-semibold underline hover:no-underline transition-all"
                style={{ color: textColor }}
              >
                {action.label}
              </button>
            )}
          </div>

          {/* Close Button */}
          <button
            onClick={handleClose}
            className="flex-shrink-0 p-1 rounded-lg hover:bg-black/10 transition-colors"
            aria-label="Close notification"
            style={{ color: textColor }}
          >
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>

        {/* Progress Bar */}
        {duration > 0 && (
          <div
            className="absolute bottom-0 left-0 h-1 transition-all duration-100 ease-linear"
            style={{
              width: `${progress}%`,
              backgroundColor: progressColor
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Toast;
